import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Search, CalendarClock, User, LayoutDashboard, LogOut, ShieldCheck,
  Menu, X, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FeedbackWidget from "@/components/app/FeedbackWidget";
import { toast } from "sonner";

const NAV_CUSTOMER = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/browse", label: "Explore", icon: Search },
  { to: "/bookings", label: "Bookings", icon: CalendarClock },
  { to: "/account", label: "Account", icon: User },
];
const NAV_PROVIDER = [
  { to: "/provider", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/provider/listings", label: "Listings", icon: Sparkles },
  { to: "/provider/bookings", label: "Bookings", icon: CalendarClock },
  { to: "/provider/kyc", label: "KYC", icon: ShieldCheck },
];
const NAV_ADMIN = [
  { to: "/admin", label: "Admin", icon: ShieldCheck, end: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  let items = NAV_CUSTOMER;
  if (user?.role === "provider") items = NAV_PROVIDER;
  else if (user?.role === "admin") items = NAV_ADMIN;

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    nav("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="glass-nav sticky top-0 z-40 border-b">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-orange text-white shadow-sm">
              <Sparkles size={18} />
            </span>
            <span className="font-heading text-xl font-extrabold tracking-tight">
              Trident<span className="text-brand-orange">DigitalPulse</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5 ${
                    isActive ? "bg-brand-ink text-white" : "text-foreground/70 hover:text-foreground"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user?.code && (
              <span
                className="hidden items-center gap-1.5 rounded-full border border-foreground/10 bg-white px-3 py-1 md:inline-flex"
                title="Your TridentDigitalPulse ID"
                data-testid="nav-id-chip"
              >
                <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">ID</span>
                <span className="font-mono text-[11px] font-bold tracking-wider">{user.code}</span>
              </span>
            )}
            {!user ? (
              <>
                <Link to="/login" className="hidden sm:inline-flex" data-testid="header-login">
                  <Button variant="ghost" className="rounded-full">Log in</Button>
                </Link>
                <Link to="/register" data-testid="header-signup">
                  <Button className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90">
                    Get started
                  </Button>
                </Link>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className="flex items-center gap-2 rounded-full border border-foreground/10 bg-white px-2 py-1.5 pr-3 text-sm font-medium hover:-translate-y-0.5 transition-transform"
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-full bg-brand-ink text-white text-xs">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    <div className="mt-1 inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                      {user.role}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.role === "customer" && (
                    <DropdownMenuItem onClick={() => nav("/bookings")} data-testid="menu-bookings">
                      <CalendarClock size={16} className="mr-2" /> My bookings
                    </DropdownMenuItem>
                  )}
                  {user.role === "provider" && (
                    <DropdownMenuItem onClick={() => nav("/provider")} data-testid="menu-provider">
                      <LayoutDashboard size={16} className="mr-2" /> Provider dashboard
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                      <ShieldCheck size={16} className="mr-2" /> Admin console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-brand-orange">
                    <LogOut size={16} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile toggle */}
            <button
              className="rounded-full border border-foreground/10 p-2 md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile expanded links */}
        {mobileOpen && (
          <div className="border-t bg-white md:hidden" data-testid="mobile-nav">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-brand-ink text-white" : "text-foreground/80 hover:bg-muted"
                    }`
                  }
                >
                  <it.icon size={18} /> {it.label}
                </NavLink>
              ))}
              {!user && (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="rounded-xl bg-muted px-3 py-2.5 text-sm font-medium">Log in</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="rounded-xl bg-brand-orange px-3 py-2.5 text-sm font-medium text-white">Get started</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6 md:pb-10">
        <Outlet key={loc.pathname} />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-3 left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-full border border-foreground/10 bg-white/90 px-2 py-1.5 shadow-lg backdrop-blur md:hidden"
        data-testid="bottom-nav"
      >
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`bottom-nav-${it.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 rounded-full px-3 py-1.5 text-[10px] font-medium ${
                isActive ? "bg-brand-ink text-white" : "text-foreground/70"
              }`
            }
          >
            <it.icon size={18} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <FeedbackWidget />
    </div>
  );
}
